import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import { isNil } from 'lodash-es';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

type ActionFn = (...args: any[]) => void | Promise<void>;

type ConfirmDialogOpenProps = {
  title: string;
  text: string | React.ReactNode;
  action: ActionFn;
  confirmButtonText?: string;
  confirmButtonColor?: 'primary' | 'error';
  forceWait?: number; // seconds
};

type ContextType = {
  isConfirmationDialogOpen: boolean;
  inProgress: boolean;
  settings: ConfirmDialogOpenProps;
  handleOpenConfirmationDialog: (props: ConfirmDialogOpenProps) => void;
  handleCloseConfirmationDialog: () => void;
  setInProgress: (inProgress: boolean) => void;
};

const ConfirmationDialogStateContext = React.createContext({} as ContextType);

export const useConfirmationDialog = () =>
  React.useContext(ConfirmationDialogStateContext);

type ContextProviderProps = {
  children: React.ReactNode;
};

export const ConfirmationDialogStateProvider = ({
  children,
}: ContextProviderProps) => {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    React.useState(false);
  const [inProgress, setInProgress] = React.useState(false);

  const [settings, setSettings] = React.useState<ConfirmDialogOpenProps>({
    title: '',
    text: '',
    action: () => {},
  });

  const handleOpenConfirmationDialog = React.useCallback(
    (newProps: ConfirmDialogOpenProps) => {
      setSettings(newProps);
      setIsConfirmationDialogOpen(true);
    },
    []
  );

  const handleCloseConfirmationDialog = React.useCallback(() => {
    setIsConfirmationDialogOpen(false);
  }, []);

  const value = React.useMemo(
    () => ({
      isConfirmationDialogOpen,
      inProgress,
      settings,
      handleOpenConfirmationDialog,
      handleCloseConfirmationDialog,
      setInProgress,
    }),
    [
      isConfirmationDialogOpen,
      inProgress,
      settings,
      handleOpenConfirmationDialog,
      handleCloseConfirmationDialog,
    ]
  );

  return (
    <ConfirmationDialogStateContext.Provider value={value}>
      {children}
      <ConfirmationDialog />
    </ConfirmationDialogStateContext.Provider>
  );
};

const useStyles = makeStyles()((theme) => ({
  dialog: {
    maxWidth: `calc(600px - ${theme.spacing(6)})`, // slightly narrower than default width
  },
}));

function ConfirmationDialog() {
  const { classes } = useStyles();
  const {
    isConfirmationDialogOpen,
    inProgress,
    settings: {
      title,
      text,
      action,
      confirmButtonText = 'Yes',
      confirmButtonColor = 'primary',
      forceWait,
    },
    handleCloseConfirmationDialog,
    setInProgress,
  } = React.useContext(ConfirmationDialogStateContext);

  const [countdown, setCountdown] = React.useState(forceWait ?? 0);
  const [prevForceWait, setPrevForceWait] = React.useState(forceWait);
  const [prevIsOpen, setPrevIsOpen] = React.useState(isConfirmationDialogOpen);

  if (prevForceWait !== forceWait || prevIsOpen !== isConfirmationDialogOpen) {
    setPrevForceWait(forceWait);
    setPrevIsOpen(isConfirmationDialogOpen);
    if (isNil(forceWait)) {
      setCountdown(0);
    } else if (isConfirmationDialogOpen) {
      setCountdown(forceWait);
    }
  }

  React.useEffect(() => {
    if (isNil(forceWait) || !isConfirmationDialogOpen) return;

    const interval = setInterval(() => {
      setCountdown((t) => {
        if (t <= 1) {
          clearInterval(interval);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [forceWait, isConfirmationDialogOpen]);

  const handleClose = () => handleCloseConfirmationDialog();

  const handleYes = async () => {
    setInProgress(true);
    await action();
    setInProgress(false);

    handleCloseConfirmationDialog();
  };

  return (
    <Dialog
      fullWidth
      open={isConfirmationDialogOpen}
      classes={{ paper: classes.dialog }}
      onClose={inProgress ? () => {} : handleClose}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{text}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button disabled={inProgress} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color={confirmButtonColor}
          disabled={inProgress || countdown !== 0}
          onClick={handleYes}
          sx={{
            fontFeatureSettings: countdown !== 0 ? '"tnum"' : undefined, // prevent layout shifts when counter is shown
          }}
        >
          {confirmButtonText}
          {countdown !== 0 ? ` (${countdown})` : null}
        </Button>
      </DialogActions>
      {inProgress ? (
        <LinearProgress />
      ) : (
        <Box
          sx={{
            height: 4,
          }}
        />
      )}
    </Dialog>
  );
}
